// src/pages/AlarmMaintenance.jsx

import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

export default function AlarmMaintenance() {
  return (
    <div className="min-h-screen bg-[#111111] text-white">

      <Helmet>
        <title>Alarm Maintenance & Support | SeguraTech</title>

        <meta
          name="description"
          content="Professional maintenance and ongoing support for Ajax alarm systems. Helping to keep your security system performing reliably long after installation."
        />

        <meta
          property="og:title"
          content="Alarm Maintenance & Support | SeguraTech"
        />

        <meta
          property="og:description"
          content="Professional maintenance and support from SeguraTech."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://www.seguratech.co.uk/alarms/maintenance"
        />

        <meta property="og:image" content="/ajax-ready.png" />

      </Helmet>

      {/* Hero */}

      <section className="max-w-5xl mx-auto px-6 pt-24 pb-20 text-center">

        <h1 className="text-4xl md:text-6xl font-extrabold text-[#C6A564] mb-8">
          Alarm Maintenance & Support
        </h1>

        <p className="text-xl text-white/80 max-w-3xl mx-auto leading-8">
          Installing your alarm system is only the beginning. Ongoing
          maintenance and professional support help ensure your security
          system continues to protect your property for years to come.
        </p>

      </section>

      {/* Main Content */}

      <section className="max-w-5xl mx-auto px-6 pb-24 space-y-20">

        {/* Ongoing Support */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Ongoing Support When You Need It
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              Our relationship with our customers doesn't end once an alarm
              system has been installed.
            </p>

            <p>
              Whether you need advice, assistance using the mobile app,
              help adding new devices or simply have a question about your
              system, we're always happy to help.
            </p>

            <p>
              We believe professional support is just as important as a
              professional installation, giving you confidence that help is
              available whenever you need it.
            </p>

          </div>

        </div>

        {/* Maintenance */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Keeping Your System Performing Reliably
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              Your alarm system quietly protects your property every day,
              often without you giving it a second thought. Regular
              maintenance helps ensure everything continues to operate as
              intended, giving you confidence that your system is ready when
              you need it most.
            </p>

            <p>
              Every maintenance visit is tailored to your installation, but
              may include:
            </p>

            <ul className="space-y-3 ml-6 list-disc">

              <li>Testing alarm devices and detectors.</li>

              <li>Checking batteries and overall system health.</li>

              <li>Confirming your alarm is communicating correctly.</li>

              <li>Checking warning devices and system operation.</li>

              <li>Reviewing system settings where appropriate.</li>

              <li>Answering any questions you may have.</li>

            </ul>

          </div>

        </div>

        {/* Remote Support */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Fast, Practical Support
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              Many questions and minor issues can be resolved without the
              need for a site visit, allowing us to provide assistance
              quickly and minimise disruption.
            </p>

            <p>
              Where a visit is required, we'll arrange a convenient time to
              investigate the issue and ensure your system is operating
              correctly.
            </p>

          </div>

        </div>

        {/* Expanding */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            As Your Property Changes
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              Your security requirements may change over time.
            </p>

            <p>
              Whether you've built an extension, converted a garage, added
              a garden office or simply want additional protection, Ajax
              systems are designed to grow with your property.
            </p>

            <p>
              We'll be happy to assess your existing system and recommend
              suitable additions whenever your requirements change.
            </p>

          </div>

        </div>

        {/* Existing Systems */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Existing Ajax Systems
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              If your Ajax alarm system was installed by another company,
              we may still be able to provide ongoing maintenance and
              support.
            </p>

            <p>
              We'll assess your existing installation, ensure everything is
              operating correctly and discuss the support options available
              to you.
            </p>

          </div>

        </div>

        {/* CTA */}

        <div className="border border-[#C6A564] rounded-2xl bg-[#1a1a1a] p-12 text-center">

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            We're Here Whenever You Need Us
          </h2>

          <p className="text-white/80 leading-8 max-w-3xl mx-auto">
            Whether you require routine maintenance, technical support or
            simply have a question about your alarm system, we'd be pleased
            to help.
          </p>

          <Link
            to="/contact"
            className="inline-block mt-10 bg-[#C6A564] text-[#111111] font-semibold px-10 py-4 rounded-xl hover:opacity-90 transition"
          >
            Contact SeguraTech
          </Link>

        </div>

      </section>

    </div>
  );
}